/**
 * The `ai` admin page: the shapes `/api/admin/ai/*` answers (proto JSON from studio-core: camelCase
 * fields, int64 as strings), the calls, and the small pure helpers the tabs share.
 */
import { adminClient, type AdminOrganizationRow } from '@/api/adminClient';
import { formatCount } from '@/lib/format';

/** An int64 arrives as a string; a small number can arrive as a number. */
export type Int = string | number;

export type AiRoute = {
  id: string;
  modelId: string;
  provider: string;
  engineModel: string;
  regionClass: string;
  retentionClass: string;
  priority?: number;
  weight?: number;
  /** `ai` leaves out a false bool, so a missing value means off. */
  enabled?: boolean;
  price?: { inputMicrosPerMtok?: Int; outputMicrosPerMtok?: Int };
  health?: { calls?: Int; errors?: Int; failovers?: Int; p50LatencyMs?: number };
};
/** The efforts the aliases page can set. `none` removes the effort: `ai` answers an alias with no `defaultEffort`, so a call with none sends none. */
export const ALIAS_EFFORTS = ['none', 'low', 'medium', 'high'] as const;
export type AiAliasEffort = (typeof ALIAS_EFFORTS)[number];
/** `defaultEffort` is the effort a call gets when it names the alias and sends none; absent means none. */
export type AiAlias = { name: string; modelId: string; maxTokensCeiling?: number; description?: string; defaultEffort?: string };
export type AiDenyRule = { id: string; pattern: string; appliesTo: string; createdBy?: string; createdAt?: string };
export type AiOrgSettings = { organizationId: string; enabled?: boolean; region?: string; zeroRetention?: boolean };

/** A stored organization is on only when `enabled` is there and true: `ai` leaves out false, so a killed organization has no `enabled`. */
export const orgIsOn = (org: AiOrgSettings): boolean => org.enabled === true;
export type AiUsageRow = {
  key?: string;
  calls?: Int;
  errors?: Int;
  refusals?: Int;
  inputTokens?: Int;
  outputTokens?: Int;
  costUsd?: string;
  p50LatencyMs?: number;
  p95LatencyMs?: number;
};
export type AiCall = {
  id: string;
  organizationId?: string;
  modelId?: string;
  routeId?: string;
  provider?: string;
  keySource?: string;
  appKey?: string;
  status?: string;
  reason?: string;
  usage?: { inputTokens?: Int; outputTokens?: Int };
  costUsd?: string;
  latencyMs?: number;
  createdAt?: string;
};
export type AiChange = { id: Int; createdAt?: string; actor?: string; onBehalfOf?: string; procedure?: string; target?: string; before?: string; after?: string };
export type AiKeyOwner = 'KEY_OWNER_COMPANY' | 'KEY_OWNER_ORG' | 'KEY_OWNER_APP' | 'KEY_OWNER_PERSON';
export type AiKeyStatus = 'KEY_STATUS_OFF' | 'KEY_STATUS_NO_CALLS' | 'KEY_STATUS_FAILING' | 'KEY_STATUS_ERRORS' | 'KEY_STATUS_WORKING';
/** One key of `ai`: facts about it, never a value. `ai` leaves out zero values, so every field but the id can be missing. */
export type AiKey = {
  keyId: string;
  provider?: string;
  owner?: AiKeyOwner;
  organizationId?: string;
  appKey?: string;
  label?: string;
  storedIn?: string;
  routeIds?: string[];
  routesOn?: number;
  calls?: Int;
  errors?: Int;
  costUsd?: string;
  lastUsedAt?: string;
  lastStatus?: string;
  lastReason?: string;
  status?: AiKeyStatus;
};
/** One monthly limit of billing. A missing `organizationId` is the default for every organization. */
export type AiSpendLimit = { organizationId?: string; product: string; monthlyLimitUsd: string; spentThisMonthUsd?: string; updatedAt?: string; updatedBy?: string };
export type AiBillingGroup = 'product' | 'app' | 'user' | 'integration';
/** One group of this month's usage; `events` is an int64, `chargeUsd` a decimal string. */
export type AiBillingRow = { key: string; events?: Int; chargeUsd?: string };
export const SPEND_PRODUCTS = ['studio', 'workflows', 'agents', 'integrations', 'ai'];
export type AiPage<K extends string, T> = { [key in K]?: T[] } & { nextPageToken?: string };

export type AiGroupBy = 'provider' | 'model' | 'route' | 'organization' | 'day' | 'hour';
export type AiWindow = '24h' | '7d' | '30d';
export const AI_WINDOWS: AiWindow[] = ['24h', '7d', '30d'];
export const AI_PROVIDERS = ['vertex', 'bedrock', 'anthropic', 'gemini'];
export const AI_REGIONS = ['global', 'us'];
export const AI_DENY_TARGETS = ['family', 'model', 'provider'];
export const AI_CALL_STATUSES = ['ok', 'failed', 'denied', 'refused'];

const query = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : '';
};
const seg = encodeURIComponent;
const request = adminClient.aiRequest.bind(adminClient);

export const aiApi = {
  routes: () => request<{ routes?: AiRoute[] }>('GET', '/routes'),
  setOrder: (modelId: string, order: { preferredProvider: string } | { routeIds: string[] }) =>
    request<{ routeIds?: string[] }>('PUT', `/models/${seg(modelId)}/route-order`, order),
  setRouteEnabled: (routeId: string, enabled: boolean) => request<{ id: string; enabled: boolean }>('PUT', `/routes/${seg(routeId)}`, { enabled }),
  aliases: (pageToken?: string) => request<AiPage<'aliases', AiAlias>>('GET', `/aliases${query({ pageToken })}`),
  putAlias: (alias: { name: string; modelId: string; maxTokensCeiling: number; description?: string; defaultEffort?: AiAliasEffort }) =>
    request<unknown>('PUT', `/aliases/${seg(alias.name)}`, alias),
  denyRules: (pageToken?: string) => request<AiPage<'denyRules', AiDenyRule>>('GET', `/deny-rules${query({ pageToken })}`),
  addDenyRule: (rule: { pattern: string; appliesTo: string }) => request<{ id?: string }>('POST', '/deny-rules', rule),
  deleteDenyRule: (id: string) => request<unknown>('DELETE', `/deny-rules/${seg(id)}`),
  orgSettings: (pageToken?: string) => request<AiPage<'orgSettings', AiOrgSettings>>('GET', `/org-settings${query({ pageToken })}`),
  putOrgSettings: (organizationId: string, settings: { region: string; enabled?: boolean; zeroRetention?: boolean }) =>
    request<unknown>('PUT', `/org-settings/${seg(organizationId)}`, settings),
  usage: (from: string, to: string, groupBy: AiGroupBy, filters?: { provider?: string; keySource?: 'company' | 'org' | 'app'; organizationId?: string; appKey?: string }) =>
    request<{ rows?: AiUsageRow[] }>('GET', `/usage${query({ from, to, groupBy, ...filters })}`),
  keys: (from: string, to: string) => request<{ keys?: AiKey[] }>('GET', `/keys${query({ from, to })}`),
  calls: (filters: Record<string, string | undefined>, pageToken?: string) => request<AiPage<'calls', AiCall>>('GET', `/calls${query({ ...filters, pageToken })}`),
  organizations: (): Promise<{ items: AdminOrganizationRow[] }> => adminClient.listOrganizations(),
  spendLimits: (organizationId?: string) => request<{ limits?: AiSpendLimit[] }>('GET', `/spend-limits${query({ organizationId })}`),
  putSpendLimit: (limit: { organizationId?: string; product: string; monthlyLimitUsd?: string; clear?: boolean }) => request<unknown>('PUT', '/spend-limits', limit),
  billingUsage: (organizationId: string, groupBy: AiBillingGroup) =>
    request<{ from: string; to: string; rows: AiBillingRow[] }>('GET', `/billing-usage${query({ organizationId, groupBy })}`),
  changes: (pageToken?: string) => request<AiPage<'changes', AiChange>>('GET', `/changes${query({ pageToken })}`),
};

export const num = (value: Int | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const WINDOW_MS: Record<AiWindow, number> = { '24h': 86_400_000, '7d': 7 * 86_400_000, '30d': 30 * 86_400_000 };

/** The time range of a window ending now, and how its chart groups: by hour for a day, by day for more. */
export function windowRange(window: AiWindow, now: Date = new Date()): { from: string; to: string; series: 'hour' | 'day' } {
  return { from: new Date(now.getTime() - WINDOW_MS[window]).toISOString(), to: now.toISOString(), series: window === '24h' ? 'hour' : 'day' };
}

/** The window a Calls filter names, as the `from` time. */
export const sinceFrom = (window: AiWindow | '1h', now: Date = new Date()): string =>
  new Date(now.getTime() - (window === '1h' ? 3_600_000 : WINDOW_MS[window])).toISOString();

export type AiTotals = { calls: number; errors: number; refusals: number; inputTokens: number; outputTokens: number; costUsd: number; p50?: number; p95?: number };

/**
 * One line for a window. `ai` gives percentiles for each group, never for a total, so the duration
 * is the slowest group's value (a ceiling), not an average.
 */
export function totalsOf(rows: AiUsageRow[]): AiTotals {
  const max = (a: number | undefined, b: number | undefined) => (b === undefined ? a : a === undefined ? b : Math.max(a, b));
  return rows.reduce<AiTotals>(
    (sum, row) => ({
      calls: sum.calls + num(row.calls),
      errors: sum.errors + num(row.errors),
      refusals: sum.refusals + num(row.refusals),
      inputTokens: sum.inputTokens + num(row.inputTokens),
      outputTokens: sum.outputTokens + num(row.outputTokens),
      costUsd: sum.costUsd + num(row.costUsd),
      p50: max(sum.p50, row.p50LatencyMs),
      p95: max(sum.p95, row.p95LatencyMs),
    }),
    { calls: 0, errors: 0, refusals: 0, inputTokens: 0, outputTokens: 0, costUsd: 0 },
  );
}

export type ModelRoutes = { modelId: string; routes: AiRoute[] };

/** The routes of each model, first choice first. `ai` already answers in model, then priority order. */
export function routesByModel(routes: AiRoute[]): ModelRoutes[] {
  const byModel = new Map<string, AiRoute[]>();
  for (const route of routes) byModel.set(route.modelId, [...(byModel.get(route.modelId) ?? []), route]);
  return [...byModel].map(([modelId, list]) => ({ modelId, routes: [...list].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)) }));
}

/** The provider of the first route that is on, else of the first route. */
export function preferredProvider(routes: AiRoute[]): string | undefined {
  return (routes.find((route) => route.enabled === true) ?? routes[0])?.provider;
}

export const providersOf = (routes: AiRoute[]): string[] => [...new Set(routes.map((route) => route.provider))];

/** The dropdown choices of a model: each provider once, marked "(off)" when all its routes are off. */
export function providerOptions(routes: AiRoute[]): Array<{ value: string; label: string }> {
  return providersOf(routes).map((value) => ({ value, label: routes.filter((route) => route.provider === value).every((route) => route.enabled !== true) ? `${value} (off)` : value }));
}

/** The route ids with the one at `index` moved by `step`, or the same list at an end. */
export function moved(ids: string[], index: number, step: -1 | 1): string[] {
  const target = index + step;
  if (index < 0 || index >= ids.length || target < 0 || target >= ids.length) return ids;
  const next = [...ids];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/** What a call cost, from the `costUsd` string (left out when zero): 4 decimals below $1, 2 from $1. */
export function costLabel(costUsd: string | number | undefined): string {
  const amount = num(costUsd);
  return `$${amount < 1 ? amount.toFixed(4) : amount.toFixed(2)}`;
}

/** Micro-USD per million tokens, as "$15.00". */
export const priceLabel = (micros: Int | undefined): string => (micros === undefined ? '—' : `$${(num(micros) / 1e6).toFixed(2)}`);

export function durationLabel(ms: number | undefined): string {
  if (ms === undefined) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${formatCount(Math.round(ms))} ms`;
}

export const regionLabel = (region: string | undefined): string => (region === 'us' ? 'US only' : region === 'global' ? 'Global' : (region ?? '—'));

/** A short name for a procedure: "/rapidsos.ai.v1.AiService/SetRouteOrder" is "SetRouteOrder". */
export const procedureLabel = (procedure: string | undefined): string => (procedure ?? '').split('/').pop() || '—';

/** Who made a change: the admin the call acted for, else the service that called. */
export const changeWho = (change: AiChange): string => change.onBehalfOf || change.actor || '—';

function parse(json: string | undefined): Record<string, unknown> | undefined {
  if (!json) return undefined;
  try {
    const value = JSON.parse(json) as unknown;
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

const isIdList = (value: unknown): value is string[] => Array.isArray(value) && value.every((id) => typeof id === 'string');

/**
 * The ids with the ending they share dropped ("-claude-opus-5-5"), so an order fits on a line. It strips
 * only when 2 or more different ids share an ending, only at a "-", and never below the first part of an id.
 */
export function shortIds(ids: string[]): Map<string, string> {
  const distinct = [...new Set(ids)];
  const out = new Map(distinct.map((id) => [id, id]));
  if (distinct.length < 2) return out;
  const last = (id: string, back: number) => id[id.length - 1 - back];
  let end = 0;
  while (distinct.every((id) => id.length > end && last(id, end) === last(distinct[0]!, end))) end += 1;
  const room = Math.min(...distinct.map((id) => (id.includes('-') ? id.length - id.indexOf('-') : 0)));
  end = Math.min(end, room);
  while (end > 0 && distinct.some((id) => id[id.length - end] !== '-')) end -= 1;
  if (end === 0) return out;
  for (const id of distinct) out.set(id, id.slice(0, id.length - end));
  return out;
}

/** What a change did, one line for each changed field: "priority 1 → 2", or "Created" or "Deleted". */
export function changeLines(change: AiChange): string[] {
  const before = parse(change.before);
  const after = parse(change.after);
  if (!before && after) return ['Created'];
  if (before && !after) return ['Deleted'];
  if (!before || !after) return [];
  const short = shortIds([...Object.values(before), ...Object.values(after)].flatMap((v) => (isIdList(v) ? v : [])));
  const show = (value: unknown) => (typeof value === 'string' ? value : Array.isArray(value) ? value.map((id) => short.get(id as string) ?? String(id)).join(' > ') : JSON.stringify(value));
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
  return keys.map((key) => `${key} ${show(before[key])} → ${show(after[key])}`);
}

/** A chart tick: "2 PM" for an hour key, "Oct 23" for a day key (both UTC, as `ai` groups them). */
export function seriesLabel(key: string | undefined, series: 'hour' | 'day'): string {
  const date = new Date(series === 'day' ? `${key}T00:00:00Z` : (key ?? ''));
  if (Number.isNaN(date.getTime())) return key ?? '';
  return series === 'day'
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : date.toLocaleTimeString('en-US', { hour: 'numeric', timeZone: 'UTC' });
}
